import ContactButtons from '../comms/ContactButtons'   // Communication
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AREAS, TITLES, cleanRights, titleRights, rightsSummary, vesselsText } from '../lib/companyMembers'

// Fleet -> Team (fleet-4, sql/fleet-4.sql): the company's main login adds the people of the management company - DPA, technical
// superintendent, accounts, fleet manager - each with their own login and rights per area and per vessel; changes or switches them off.
// The login is made by the server (api/admin.js my_team_create); the rights are the database's (hc_member_add / _update / _set_active).
const CSS = C => `
.hct-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;justify-content:space-between;margin-bottom:12px}
.hct-bar p{margin:0;font-size:12.5px;color:${C.muted};max-width:720px;line-height:1.45}
.hct-btn{background:${C.blue};border:1px solid ${C.blue};color:#fff;border-radius:9px;padding:8px 14px;font:600 12.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hct-btn:disabled{opacity:.45;cursor:default}
.hct-ghost{background:transparent;border:1px solid ${C.line};color:${C.text};border-radius:8px;padding:6px 10px;font:600 11.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hct-ghost:hover{border-color:${C.blue}}
.hct-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:4px 14px}
.hct-row{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,1.6fr) minmax(0,.9fr) auto;gap:12px;align-items:center;padding:12px 4px;border-top:1px solid ${C.line}}
.hct-row:first-child{border-top:none}
.hct-row.off{opacity:.55}
.hct-name{font-weight:700;font-size:13.5px;overflow-wrap:anywhere}
.hct-sub{font-size:11.5px;color:${C.muted};margin-top:3px;overflow-wrap:anywhere;line-height:1.4}
.hct-acts{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.hct-pill{display:inline-flex;font-size:10.5px;font-weight:700;padding:2px 8px;border-radius:10px;margin-left:8px;vertical-align:middle}
.hct-panel{background:var(--bg);border:1px solid ${C.blue}55;border-radius:14px;padding:16px;margin-bottom:14px}
.hct-panel h3{margin:0 0 4px;font-size:15px}
.hct-panel>p{margin:0 0 12px;font-size:12px;color:${C.muted};line-height:1.45}
.hct-f{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:12px}
.hct-f label,.hct-area label{display:flex;flex-direction:column;gap:5px;font-size:11.5px;color:${C.muted};font-weight:600;min-width:0}
.hct-in{background:${C.card};color:${C.text};border:1px solid ${C.line};border-radius:9px;padding:8px 10px;font:500 12.5px/1.2 inherit;font-family:inherit;min-width:0;width:100%;box-sizing:border-box}
.hct-areas{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-bottom:12px}
.hct-area{border:1px solid ${C.line};border-radius:11px;padding:10px 12px;min-width:0}
.hct-area b{font-size:12.5px}
.hct-area small{display:block;font-size:11px;color:${C.muted};margin:2px 0 8px;line-height:1.35}
.hct-seg{display:flex;flex-wrap:wrap;gap:4px}
.hct-seg button{background:transparent;border:1px solid ${C.line};color:${C.muted};border-radius:7px;padding:5px 10px;font:600 11.5px/1 inherit;font-family:inherit;cursor:pointer}
.hct-seg button.on{background:${C.blue};border-color:${C.blue};color:#fff}
.hct-lim{display:grid;grid-template-columns:minmax(0,1fr) 84px;gap:6px;margin-top:8px}
.hct-ves{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px}
.hct-ves button{background:transparent;border:1px solid ${C.line};color:${C.text};border-radius:16px;padding:6px 12px;font:600 12px/1 inherit;font-family:inherit;cursor:pointer}
.hct-ves button.on{background:rgba(59,123,255,.18);border-color:${C.blue};color:${C.blue}}
.hct-msg{font-size:12px;margin-top:8px;line-height:1.45}
@media (max-width:900px){.hct-row{grid-template-columns:minmax(0,1fr);gap:6px}.hct-acts{justify-content:flex-start}.hct-f{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:600px){.hct-f,.hct-areas{grid-template-columns:minmax(0,1fr)}}
`
const errText = e => String((e && e.message) || e || '').replace(/^.*?ERROR:\s*/, '')
async function api(body) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch('/api/admin', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') }, body: JSON.stringify(body) })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(j.error || ('HTTP ' + res.status))
  return j
}
function genPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  const a = new Uint32Array(12); window.crypto.getRandomValues(a)
  return Array.from(a, n => chars[n % chars.length]).join('')
}

function MemberForm({ C, fleet, member, onDone, onCancel }) {
  const editing = !!member
  const [name, setName] = useState(member ? member.name : '')
  const [email, setEmail] = useState(member ? member.email || '' : '')
  const [password, setPassword] = useState(editing ? '' : genPassword())
  const [title, setTitle] = useState(member ? member.title || '' : 'DPA')
  const [rights, setRights] = useState(cleanRights(member ? member.rights : titleRights('DPA')))
  const [vessels, setVessels] = useState(member && member.vessel_ids ? member.vessel_ids.map(String) : null)   // null = all
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const pickTitle = t => { setTitle(t); if (TITLES.some(x => x.key === t)) setRights(titleRights(t)) }
  const set = (k, v) => setRights(r => ({ ...r, [k]: v }))
  const toggle = id => setVessels(cur => { const s = new Set(cur || []); s.has(id) ? s.delete(id) : s.add(id); return [...s] })
  async function save() {
    setBusy(true); setMsg(null)
    try {
      const vs = vessels == null ? null : vessels
      if (vs && !vs.length) throw new Error('Choose at least one vessel (or all vessels)')
      if (editing) {
        const { error } = await supabase.rpc('hc_member_update', { p_id: member.id, p_name: name, p_title: title, p_rights: rights, p_vessels: vs })
        if (error) throw error
      } else {
        await api({ action: 'my_team_create', name, email, password, title, rights, vessel_ids: vs })
      }
      onDone(editing ? 'Saved.' : name + ' can log in now with ' + email + ' and the first password ' + password + ' (give it to them; they can change it after logging in).')
    } catch (e) { setMsg({ err: true, text: errText(e) }) }
    setBusy(false)
  }
  return (
    <div className="hct-panel" data-panel="member">
      <h3>{editing ? 'Change ' + member.name : 'Add a person of the company'}</h3>
      <p>Their own login, their own rights. Management rules stay the same for them: they never set a risk assessment or SOP Active (the Master does), and they approve purchase orders only up to their limit.</p>
      <div className="hct-f">
        <label>Name<input className="hct-in" value={name} onChange={e => setName(e.target.value)} placeholder="First and last name" maxLength={120} /></label>
        <label>Role<select className="hct-in" value={TITLES.some(t => t.key === title) ? title : '_other'} onChange={e => pickTitle(e.target.value === '_other' ? '' : e.target.value)}>
          {TITLES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}<option value="_other">Other…</option></select></label>
        {!TITLES.some(t => t.key === title) && <label>Role (own words)<input className="hct-in" value={title} onChange={e => setTitle(e.target.value)} maxLength={60} placeholder="e.g. Crew manager" /></label>}
        {!editing && <label>E-mail (their login)<input className="hct-in" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" /></label>}
        {!editing && <label>First password<input className="hct-in" value={password} onChange={e => setPassword(e.target.value)} minLength={8} /></label>}
      </div>
      <div className="hct-areas">
        {AREAS.map(a => (
          <div key={a.key} className="hct-area" data-area={a.key}>
            <b>{a.label}</b><small>{a.help}</small>
            <div className="hct-seg">{a.levels.map(l => <button key={l} type="button" className={rights[a.key] === l ? 'on' : ''} onClick={() => set(a.key, l)}>{a.words[l]}</button>)}</div>
            {a.key === 'approvals' && rights.approvals === 'full' && (
              <div className="hct-lim">
                <label>Approval limit (empty = no limit)<input className="hct-in" inputMode="decimal" value={rights.approval_limit ?? ''} onChange={e => set('approval_limit', e.target.value.trim() === '' ? null : e.target.value.replace(/[^\d.]/g, ''))} placeholder="No limit" /></label>
                <label>Currency<input className="hct-in" value={rights.approval_currency} onChange={e => set('approval_currency', e.target.value.toUpperCase().slice(0, 3))} maxLength={3} /></label>
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600, marginBottom: 6 }}>Vessels</div>
      <div className="hct-ves">
        <button type="button" className={vessels == null ? 'on' : ''} onClick={() => setVessels(null)}>All vessels{vessels == null ? ' ✓' : ''}</button>
        {fleet.map(v => <button key={v.id} type="button" className={vessels && vessels.includes(String(v.id)) ? 'on' : ''} onClick={() => toggle(String(v.id))}>{v.vessel_name}</button>)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="hct-btn" disabled={busy || name.trim().length < 2 || (!editing && (!/@/.test(email) || password.length < 8))} onClick={save}>{busy ? 'Saving…' : editing ? 'Save' : 'Add to the team'}</button>
        <button className="hct-ghost" onClick={onCancel}>Cancel</button>
      </div>
      {msg && <div className="hct-msg" style={{ color: msg.err ? C.red : C.green }}>{msg.text}</div>}
    </div>
  )
}

export default function FleetTeam({ fleet, palette: C }) {
  const [team, setTeam] = useState(null)
  const [missing, setMissing] = useState(false)
  const [edit, setEdit] = useState(null)   // null | 'new' | member
  const [note, setNote] = useState(null)
  async function load() {
    const { data, error } = await supabase.from('company_members').select('*').order('active', { ascending: false }).order('created_at')
    if (error) { setMissing(/company_members|schema cache|does not exist/i.test(error.message)); setTeam([]); return }
    setTeam(data || [])
  }
  useEffect(() => { load() }, [])
  async function switchActive(m) {
    if (m.active && !window.confirm('Switch off ' + m.name + '? They are locked out at once (no vessel, no fleet). You can switch them on again.')) return
    const { error } = await supabase.rpc('hc_member_set_active', { p_id: m.id, p_active: !m.active })
    setNote(error ? { err: true, text: errText(error) } : { text: m.name + (m.active ? ' is switched off.' : ' is switched on again.') })
    load()
  }
  async function newPassword(m) {
    const pw = genPassword()
    if (!window.confirm('Give ' + m.name + ' a new password?\n\n' + pw + '\n\nThe old one stops working.')) return
    try { await api({ action: 'my_team_password', member_id: m.id, password: pw }); setNote({ text: 'New password for ' + m.name + ': ' + pw }) } catch (e) { setNote({ err: true, text: errText(e) }) }
  }
  return (
    <div data-tab="team">
      <style>{CSS(C)}</style>
      <div className="hct-bar">
        <p>The people of your company, each with their own login. Their rights are per area and per vessel; the change history names who did what.</p>
        {!missing && <button className="hct-btn" onClick={() => { setEdit('new'); setNote(null) }}>+ Add a person</button>}
      </div>
      {missing && <div className="hct-card" style={{ padding: 18, color: C.muted, fontSize: 13 }}>The team needs <b>sql/fleet-4.sql</b> on the database first.</div>}
      {edit && <MemberForm C={C} fleet={fleet} member={edit === 'new' ? null : edit} onCancel={() => setEdit(null)} onDone={t => { setEdit(null); setNote({ text: t }); load() }} />}
      {note && <div className="hct-msg" style={{ color: note.err ? C.red : C.green, marginBottom: 10 }}>{note.text}</div>}
      {!missing && (
        <div className="hct-card">
          {team == null ? <div style={{ padding: 14, color: C.muted, fontSize: 12.5 }}>Loading&hellip;</div>
            : !team.length ? <div style={{ padding: 18, color: C.muted, fontSize: 13 }}>Only your main login so far. Add your DPA, technical superintendent or accounts.</div>
            : team.map(m => (
              <div key={m.id} className={'hct-row' + (m.active ? '' : ' off')} data-member={m.name}>
                <div><div className="hct-name">{m.name}{!m.active && <span className="hct-pill" style={{ background: C.red + '22', color: C.red }}>Switched off</span>}</div>
                  <div className="hct-sub">{[m.title, m.email].filter(Boolean).join(' · ')} <ContactButtons compact email={m.email} subject="Hey Captain" /></div></div>
                <div className="hct-sub" style={{ marginTop: 0, color: C.text }}>{rightsSummary(m.rights)}</div>
                <div className="hct-sub" style={{ marginTop: 0 }}>{vesselsText(m, fleet)}</div>
                <div className="hct-acts">
                  {m.active && <button className="hct-ghost" onClick={() => { setEdit(m); setNote(null) }}>Change</button>}
                  {m.active && <button className="hct-ghost" onClick={() => newPassword(m)}>New password</button>}
                  <button className="hct-ghost" style={{ color: m.active ? C.red : C.green }} onClick={() => switchActive(m)}>{m.active ? 'Switch off' : 'Switch on'}</button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
